<script>
import { useVuelidate } from '@vuelidate/core';
import { email, maxLength, minLength, required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import FormInput from '../../../../components/Form/Input.vue';
import { BaseButton } from '../../../../components/Button';
import { isValidPassword } from 'shared/helpers/Validators';
import GoogleOAuthButton from '../../../../../../../v3/components/GoogleOauth/Button.vue';
import { register } from 'v3/api/auth';
import GapifyRulesConfirmation from '../../../RulesConfirmation.vue';
import PhoneInput from '../../../../components/Form/PhoneInput.vue';
import FormSelect from '../../../../components/Form/Select.vue';
import ActivityAreas from '../../activityAreas';

export default {
  components: {
    GapifyRulesConfirmation,
    PhoneInput,
    FormInput,
    GoogleOAuthButton,
    BaseButton,
    FormSelect,
    VueHcaptcha,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: {
        accountName: 'default-company',
        activityArea: ActivityAreas.TECH,
        fullName: '',
        email: '',
        phoneNumber: '',
        completePhoneNumber: '',
        password: '',
        confirmPassword: '',
        hCaptchaClientResponse: '',
      },
      checkedRules: false,
      didCaptchaReset: false,
      isSignupInProgress: false,
      dialCode: '',
      error: '',
    };
  },
  validations() {
    return {
      credentials: {
        accountName: {
          required,
          minLength: minLength(2),
        },
        phoneNumber: {
          validatePhoneNumber(value) {
            const phoneRegex = /^[0-9]*$/;
            return !(value && !phoneRegex.test(value));
          },
          maxLength: maxLength(15),
          minLength: minLength(10),
        },
        activityArea: {
          required,
          minLength: minLength(2),
        },
        fullName: {
          required,
          minLength: minLength(2),
        },
        email: {
          required,
          email,
        },
        password: {
          required,
          isValidPassword,
          minLength: minLength(6),
        },
        confirmPassword: {
          required,
          sameAsPassword(value) {
            return value === this.credentials.password;
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    termsLink() {
      return this.$t('REGISTER.TERMS_ACCEPT')
        .replace('https://www.chatwoot.com/terms', this.globalConfig.termsURL)
        .replace(
          'https://www.chatwoot.com/privacy-policy',
          this.globalConfig.privacyURL
        );
    },
    hasAValidCaptcha() {
      if (this.globalConfig.hCaptchaSiteKey) {
        return !!this.credentials.hCaptchaClientResponse;
      }
      return true;
    },
    getActivityAreasOptions() {
      return [
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.ONLINE_EDUCATION'),
          value: ActivityAreas.ONLINE_EDUCATION,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.E_COMMERCE'),
          value: ActivityAreas.E_COMMERCE,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.LOGISTICS'),
          value: ActivityAreas.LOGISTICS,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.ONLINE_SERVICE'),
          value: ActivityAreas.ONLINE_SERVICE,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.CONSULTING'),
          value: ActivityAreas.CONSULTING,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.ADVERTISING'),
          value: ActivityAreas.ADVERTISING,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.TECH'),
          value: ActivityAreas.TECH,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.FINE_TECH'),
          value: ActivityAreas.FINE_TECH,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.PERSONAL_BUSINESS'),
          value: ActivityAreas.PERSONAL_BUSINESS,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.MANUFACTURING'),
          value: ActivityAreas.MANUFACTURING,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.CONTRACTING'),
          value: ActivityAreas.CONTRACTING,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.TRADE'),
          value: ActivityAreas.TRADE,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.MIGRATION'),
          value: ActivityAreas.MIGRATION,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.SERVICES'),
          value: ActivityAreas.SERVICES,
        },
        {
          label: this.$t('REGISTER.ACTIVITY_AREA.OPTIONS.OTHERS'),
          value: ActivityAreas.OTHERS,
        },
      ];
    },
    phoneNumberErrorText() {
      const { phoneNumber } = this.v$.credentials;
      if (!phoneNumber.$error) {
        return '';
      }
      if (phoneNumber.minLength.$invalid) {
        return this.$t('REGISTER.PHONE_NUMBER.MIN_LENGTH_ERROR');
      }
      if (phoneNumber.maxLength.$invalid) {
        return this.$t('REGISTER.PHONE_NUMBER.MAX_LENGTH_ERROR');
      }
      if (phoneNumber.validatePhoneNumber.$invalid) {
        return this.$t('REGISTER.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    passwordErrorText() {
      const { password } = this.v$.credentials;
      if (!password.$error) {
        return '';
      }
      if (password.minLength.$invalid || password.required.$invalid) {
        return this.$t('REGISTER.PASSWORD.ERROR');
      }
      if (password.isValidPassword.$invalid) {
        return this.$t('REGISTER.PASSWORD.IS_INVALID_PASSWORD');
      }
      return '';
    },
    showGoogleOAuth() {
      return Boolean(window.chatwootConfig.googleOAuthClientId);
    },
    confirmPasswordErrorText() {
      const { confirmPassword } = this.v$.credentials;
      if (!confirmPassword.$error) {
        return '';
      }
      if (confirmPassword.required.$invalid) {
        return this.$t('REGISTER.CONFIRM_PASSWORD.REQUIRED_ERROR');
      }
      if (confirmPassword.sameAsPassword.$invalid) {
        return this.$t('REGISTER.CONFIRM_PASSWORD.MISMATCH_ERROR');
      }
      return '';
    },
  },
  methods: {
    async submit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        this.resetCaptcha();
        return;
      }
      this.isSignupInProgress = true;
      try {
        this.credentials.completePhoneNumber = `${this.dialCode}${this.credentials.phoneNumber}`;

        const { confirmPassword, ...payload } = this.credentials;
        const response = await register(payload);

        // پاک کردن داده‌های onboarding قبلی
        localStorage.removeItem('onboarding_activity_area');
        localStorage.removeItem('onboarding_store_type');

        // هدایت به صفحه onboarding - مرحله انتخاب حوزه فعالیت
        if (response?.data?.account_id) {
          window.location = `/app/accounts/${response.data.account_id}/onboarding/select-activity`;
        } else {
          // fallback به مسیر قدیمی
          window.location = '/app/auth/onboarding/step-1';
        }
      } catch (error) {
        let errorMessage =
          error?.message || this.$t('REGISTER.API.ERROR_MESSAGE');
        this.resetCaptcha();
        useAlert(errorMessage);
      } finally {
        this.isSignupInProgress = false;
      }
    },
    updateDialCode(code) {
      this.dialCode = code;
    },
    onRecaptchaVerified(token) {
      this.credentials.hCaptchaClientResponse = token;
      this.didCaptchaReset = false;
    },
    resetCaptcha() {
      if (!this.globalConfig.hCaptchaSiteKey) {
        return;
      }
      this.$refs.hCaptcha.reset();
      this.credentials.hCaptchaClientResponse = '';
      this.didCaptchaReset = true;
    },
    handleEnterKey(event) {
      if (
        event.key === 'Enter' &&
        !this.isSignupInProgress &&
        this.hasAValidCaptcha &&
        this.checkedRules &&
        !this.v$.$invalid
      ) {
        event.preventDefault();
        this.submit();
      }
    },
  },
};
</script>

<template>
  <form
    class="flex flex-col min-[1440px]:mt-6 mt-4 min-h-0 max-[1439px]:justify-between max-[1439px]:h-full"
    @submit.prevent="submit"
  >
    <div class="flex flex-col px-0 gap-6">
      <FormInput
        v-model="credentials.fullName"
        name="full_name"
        class="flex-1"
        required
        :class="{ error: v$.credentials.fullName.$error }"
        :label="$t('REGISTER.FULL_NAME.LABEL')"
        :placeholder="$t('REGISTER.FULL_NAME.PLACEHOLDER')"
        :has-error="v$.credentials.fullName.$error"
        :error-message="$t('REGISTER.FULL_NAME.ERROR')"
        is-first-item
        @blur="v$.credentials.fullName.$touch"
      />
      <div
        class="grid grid-cols-1 min-[1440px]:grid-cols-2 min-[1440px]:gap-3 gap-6 max-[959px]:gap-6"
      >
        <FormInput
          v-model="credentials.email"
          type="email"
          required
          name="email_address"
          :class="{ error: v$.credentials.email.$error }"
          :label="$t('REGISTER.EMAIL.LABEL')"
          :placeholder="$t('REGISTER.EMAIL.PLACEHOLDER')"
          :has-error="v$.credentials.email.$error"
          :error-message="$t('REGISTER.EMAIL.ERROR')"
          autocomplete="email"
          is-first-item
          @blur="v$.credentials.email.$touch"
        />
        <PhoneInput
          v-model="credentials.phoneNumber"
          name="phone_number"
          :class="{ error: v$.credentials.phoneNumber.$error }"
          :has-error="v$.credentials.phoneNumber.$error"
          :error-message="phoneNumberErrorText"
          :label="$t('REGISTER.PHONE_NUMBER.LABEL')"
          autocomplete="tel"
          is-first-item
          @blur="v$.credentials.phoneNumber.$touch"
          @set-code="updateDialCode"
        />
      </div>
      <div v-if="false" class="grid grid-cols-2 gap-2">
        <FormSelect
          v-model="credentials.activityArea"
          name="activity_area"
          required
          :options="getActivityAreasOptions"
          class="w-full h-full flex-1"
          :class="{ error: v$.credentials.activityArea.$error }"
          :label="$t('REGISTER.ACTIVITY_AREA.LABEL')"
          :placeholder="$t('REGISTER.ACTIVITY_AREA.PLACEHOLDER')"
          :has-error="v$.credentials.activityArea.$error"
          :error-message="$t('REGISTER.ACTIVITY_AREA.ERROR')"
          @blur="v$.credentials.activityArea.$touch"
        />
        <FormInput
          v-model="credentials.accountName"
          name="account_name"
          required
          :class="{ error: v$.credentials.accountName.$error }"
          :label="$t('REGISTER.COMPANY_NAME.LABEL')"
          :placeholder="$t('REGISTER.COMPANY_NAME.PLACEHOLDER')"
          :has-error="v$.credentials.accountName.$error"
          :error-message="$t('REGISTER.COMPANY_NAME.ERROR')"
          @blur="v$.credentials.accountName.$touch"
        />
      </div>
      <div
        class="grid grid-cols-1 min-[960px]:grid-cols-2 min-[1440px]:gap-3 gap-4 max-[959px]:gap-6"
      >
        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          required
          placeholder-marquee
          :class="{ error: v$.credentials.password.$error }"
          :label="$t('REGISTER.PASSWORD.LABEL')"
          :placeholder="$t('REGISTER.PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.password.$error"
          :error-message="passwordErrorText"
          autocomplete="new-password"
          is-first-item
          @blur="v$.credentials.password.$touch"
          @keydown.enter="handleEnterKey"
        />

        <FormInput
          v-model="credentials.confirmPassword"
          type="password"
          name="confirm_password"
          required
          :class="{ error: v$.credentials.confirmPassword.$error }"
          :label="$t('REGISTER.CONFIRM_PASSWORD.LABEL')"
          :placeholder="$t('REGISTER.CONFIRM_PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.confirmPassword.$error"
          :error-message="confirmPasswordErrorText"
          autocomplete="new-password"
          is-first-item
          @blur="v$.credentials.confirmPassword.$touch"
          @keydown.enter="handleEnterKey"
        />
      </div>
      <div v-if="globalConfig.hCaptchaSiteKey" class="mb-3">
        <VueHcaptcha
          ref="hCaptcha"
          :class="{ error: !hasAValidCaptcha && didCaptchaReset }"
          :sitekey="globalConfig.hCaptchaSiteKey"
          @verify="onRecaptchaVerified"
        />
        <span
          v-if="!hasAValidCaptcha && didCaptchaReset"
          class="text-xs text-red-400"
        >
          {{ $t('SET_NEW_PASSWORD.CAPTCHA.ERROR') }}
        </span>
      </div>
      <GoogleOAuthButton v-if="showGoogleOAuth" class="flex-col-reverse">
        {{ $t('REGISTER.OAUTH.GOOGLE_SIGNUP') }}
      </GoogleOAuthButton>
    </div>

    <div
      class="shrink-0 flex min-[1440px]:flex-row flex-col justify-end min-[1440px]:justify-between mt-4 min-[1440px]:mt-10 bp1440:items-center items-start pb-6 gap-4"
    >
      <GapifyRulesConfirmation
        v-model:checked-rules="checkedRules"
        class="mt-0"
      />
      <BaseButton
        status="gapify"
        type="Fill"
        size="Default"
        :text="$t('REGISTER.SUBMIT')"
        :disabled="
          isSignupInProgress ||
          !hasAValidCaptcha ||
          !checkedRules ||
          v$.$invalid
        "
        :loading="isSignupInProgress"
        button-type="submit"
        class="flex max-[1439px]:!w-full md:w-auto"
      />
    </div>
  </form>
</template>

<style scoped lang="scss">
.h-captcha--box {
  &::v-deep .error {
    iframe {
      border: 1px solid var(--r-500);
      border-radius: var(--border-radius-normal);
    }
  }
}
</style>
